import { useEffect, useState } from "react";
import { IconEye, IconFileDescription, IconList } from "@tabler/icons-react";
import { adminProductRequestsService } from "../../../api_client";
import type { AdminDatasheetExtractionDto } from "../../../../api";
import { Banner } from "../../../../components/common/banner";
import { Button } from "../../../../components/common/button";
import { Heading, Text } from "../../../../components/common/typography";
import { IconPill } from "../../../../components/common/icon-pill";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { DatasheetPreviewModal } from "../../../products/presentation/components/datasheet-preview-modal";
import { AdminDatasheetExtractionDialog } from "./admin-datasheet-extraction-dialog";

interface Props {
  requestId: string;
  /** Optional legacy datasheet URL — shown alongside the PDF when both exist. */
  legacyDatasheetUrl?: string | null;
}

export function AdminDatasheetCard({ requestId, legacyDatasheetUrl }: Props) {
  const [data, setData] = useState<AdminDatasheetExtractionDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [extractedOpen, setExtractedOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    adminProductRequestsService
      .adminGetProductRequestDatasheetExtraction(requestId)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load datasheet info.");
      });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  // Hide entirely when there's neither a PDF nor a legacy URL.
  if (data && data.status === "none" && !legacyDatasheetUrl) return null;

  return (
    <div className="rounded-2xl border border-borderDefault bg-bgWhite shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)] w-full">
      <div className="px-6 pt-5 pb-4">
        <Heading variant="h3" tone="dark">Product datasheet</Heading>
      </div>
      <div className="border-t border-borderDefault px-6 py-4">
        {error ? (
          <Text variant="body" tone="body" className="!text-accentError">{error}</Text>
        ) : !data ? (
          <ShimmerLoader message="Loading datasheet info…" />
        ) : (
          <div className="flex flex-col gap-4">
            {data.downloadUrl && data.fileName ? (
              <div className="flex items-center gap-3 rounded-xl border border-borderDefault px-4 py-3">
                <IconPill size={36} tone="light">
                  <IconFileDescription size={20} />
                </IconPill>
                <div className="flex min-w-0 flex-1 flex-col">
                  <Text variant="body" tone="dark" className="truncate">{data.fileName}</Text>
                  <Text variant="bodySm" tone="muted">PDF datasheet uploaded by seller</Text>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<IconEye size={18} />}
                    onClick={() => setPreviewOpen(true)}
                  >
                    Preview
                  </Button>
                  {data.status === "success" && data.aiSummary ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<IconList size={18} />}
                      onClick={() => setExtractedOpen(true)}
                    >
                      View extracted data
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {data.status === "failed" ? (
              <Banner tone="error">
                AI extraction failed{data.errorMessage ? `: ${data.errorMessage}` : "."} The seller's autofill dialog was skipped — review the PDF manually.
              </Banner>
            ) : data.status === "pending" && data.downloadUrl ? (
              <Banner tone="success">
                Extraction is still running. Preview the PDF now; the AI summary will appear once Gemini finishes.
              </Banner>
            ) : data.status === "none" && !data.downloadUrl ? (
              <Text variant="bodySm" tone="muted">
                No PDF datasheet uploaded for this request.
              </Text>
            ) : null}

            {legacyDatasheetUrl ? (
              <div className="flex flex-col gap-1">
                <Text variant="bodySm" tone="body">External datasheet URL</Text>
                <a
                  href={legacyDatasheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accentCyan hover:underline"
                >
                  <Text variant="body" tone="inherit" className="truncate">{legacyDatasheetUrl}</Text>
                </a>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {previewOpen && data?.downloadUrl && data.fileName ? (
        <DatasheetPreviewModal
          url={data.downloadUrl}
          fileName={data.fileName}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}

      {extractedOpen && data?.aiSummary ? (
        <AdminDatasheetExtractionDialog
          aiSummary={data.aiSummary}
          extractedText={data.extractedText}
          provider={data.provider}
          model={data.model}
          costUSD={data.costUSD}
          createdAt={data.createdAt}
          onClose={() => setExtractedOpen(false)}
        />
      ) : null}
    </div>
  );
}
